import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { DistributionApiService } from './../distribution/distribution-api.service';
import { UserService } from 'src/app/services/user.service';
import { GlobalService } from 'src/app/services/global.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'sd-releases',
  templateUrl: './releases.component.html',
  styleUrls: ['./releases.component.scss']
})
export class ReleasesComponent implements OnInit {
  userSlug: string = '';
  songList: any[] = [];
  offset: number = 0;
  limit: number = 10;
  totalCount: number = 0;
  songsLoaded: boolean = false;
  moreSongsLoading: boolean = false;
  selectedSong: any = null;
  private targetSlug: string | null = null;
  private targetIsAlbum: boolean = false;

  songStatus = [
    { "status": "Drafts", "on": 1 },
    { "status": "Processed", "on": 0 },
    { "status": "Scheduled", "on": 0 },
    { "status": "Live", "on": 0 }
  ];

  constructor(
    private api: DistributionApiService,
    private user: UserService,
    public global: GlobalService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    const mdata = { 'title': 'Distribute Your Music | Songdew.com' };
    this.global.setMeta(mdata);
    this.userSlug = this.user.slug;
  }

  ngOnInit() {
    const slug = this.route.snapshot.params['slug'];
    if (slug) {
      this.targetSlug = slug;
      this.targetIsAlbum = this.route.snapshot.queryParams['isalbum'] === '1';
      this.loadSingleRelease(this.targetSlug, this.targetIsAlbum);
    } else {
      this.getDistributedList();
    }

    this.route.params.subscribe(params => {
      const slug = params['slug'] || null;
      if (slug && slug !== this.targetSlug) {
        this.targetSlug = slug;
        this.targetIsAlbum = this.route.snapshot.queryParams['isalbum'] === '1';
        this.loadSingleRelease(this.targetSlug, this.targetIsAlbum);
      } else if (!slug && (this.targetSlug || this.selectedSong)) {
        this.targetSlug = null;
        this.selectedSong = null;
        this.getDistributedList();
      }
    });

    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        const url = (event.urlAfterRedirects || event.url || '').split('?')[0];
        if (url === '/distribute/my-releases' && this.selectedSong) {
          this.selectedSong = null;
          this.targetSlug = null;
          this.getDistributedList();
        }
      }
    });
  }

  private loadSingleRelease(slug: string, isAlbum: boolean, isRetry: boolean = false) {
    this.songsLoaded = false;
    this.api.getDistributedReleaseBySlug(slug, isAlbum).subscribe(
      (res: any) => {
        const raw = res?.results?.distribution?.data?.[0];
        if (raw) {
          this.selectedSong = this.mapDistributionItem(raw);
          this.songsLoaded = true;
        } else if (!isRetry) {
          this.loadSingleRelease(slug, !isAlbum, true);
        } else {
          this.targetSlug = null;
          this.getDistributedList();
        }
      },
      (err: HttpErrorResponse) => {
        if (!isRetry) {
          this.loadSingleRelease(slug, !isAlbum, true);
          return;
        }
        console.error('Error fetching release by slug:', err);
        this.songsLoaded = true;
        this.getDistributedList();
      }
    );
  }

  getDistributedList(moreSongsLoading: boolean = false) {
    if (moreSongsLoading) {
      this.moreSongsLoading = true;
      this.offset++;
    } else {
      this.songList = [];
      this.offset = 0;
      this.songsLoaded = false;
    }

    this.api.getDistributedList(this.userSlug, this.offset, this.limit).subscribe(
      (res: any) => {
        if (res.status === true) {
          const newSongs = res.results.distribution.data.map((song: any) => this.mapDistributionItem(song));

          this.songList = this.songList.concat(newSongs);
          this.totalCount = res.results.distribution.total_count;
        }

        if (moreSongsLoading) {
          this.moreSongsLoading = false;
        } else {
          this.songsLoaded = true;
        }
      },
      (err: HttpErrorResponse) => {
        this.moreSongsLoading = false;
        this.songsLoaded = true;
        console.error('Error fetching distributed list:', err);
      }
    );
  }

  private mapDistributionItem(song: any): any {
    if (song.dist_status?.length) {
      song.distribution_status = [...song.dist_status];
    }

    let issue = {
      artworkIssue: song?.artIssue ? song.artIssue[0]?.r : 0,
      pdlIssue: song?.pdlIssue ? song.pdlIssue.some((track: any) => track.issue.length > 0) : 0,
      mIssue: song?.pdlIssue ? song?.pdlIssue[0]?.missue?.some((iss: any) => iss.issue == 'Clarifications Required-Inconsistent Metadata' || iss.issue == 'Clarifications Required-Legal Issues') > 0 : 0
    };

    if (song.mid === 0) {
      song.distribution_status = [...this.songStatus];
    } else if (song.mid !== 0 && (!song.distribution_status || song.distribution_status.length === 0)) {
      song.distribution_status = this.songStatus.map((status, index) => {
        if (index === 0) {
          return { ...status, status: 'Submitted', on: 1 };
        }
        return { ...status };
      });
    }

    if (song.distribution_status?.[2]?.status === 'Clarifications Required') {
      song.distribution_status[2].on = 1;
    }
    if (song.album_id > 0 && !issue.artworkIssue && !issue.pdlIssue && !issue.mIssue) {
      if (song.distribution_status?.[2]?.status === 'Clarifications Required') {
        song.distribution_status[2].status = 'Scheduled';
        song.distribution_status[2].on = 0;
      }
    }

    this.setPlatformLinks(song);

    if (song.agreements && song.agreements.length > 0) {
      const processed = Array.isArray(song.agreements) ? song.agreements : [];
      processed.forEach((a: any) => {
        a.song_id = song.mid;
        a.song_slug = song.slug;
        a.song_title = song.title;
        a.album_id = song.album_id;
      });
      song.processedAgreements = processed;
      song.agreementPreview = (song.agreementPreview || '').replace(/(style=")(.*)(")/gi, '');
      song.agreementPreviews = processed.map((a: any) => a?.agreement || '');
    }

    return { ...song, trackIssue: issue };
  }

  setPlatformLinks(song: any) {
    const allowedPlatforms = ["spotify", "appleMusic", "itunes", "youtube", "amazonStore", "soundcloud", "youtubeMusic", "pandora", "deezer"];
    if (song.liveUrl) {
      const platformLinks = [];
      for (const [platform, value] of Object.entries(song.liveUrl)) {
        if (allowedPlatforms.includes(platform) && value && typeof value === 'object' && 'url' in value) {
          platformLinks.push({
            name: platform,
            url: (value as { url: string }).url
          });
        }
      }
      song.platformLinks = platformLinks;
    } else {
      song.platformLinks = [];
    }
  }

  onSelectRelease(song: any) {
    this.selectedSong = song;
    this.targetSlug = song.slug;
    this.targetIsAlbum = song.album_id > 0;
    this.router.navigate(['/distribute/my-releases', song.slug], {
      queryParams: { isalbum: this.targetIsAlbum ? '1' : '0' }
    });
  }

  onBackToList() {
    this.selectedSong = null;
    this.targetSlug = null;
    this.router.navigate(['/distribute/my-releases']);
  }

  onDeleteRelease(event: { element: any, slug: string, isAlbum: boolean }) {
    this.api.deleteDistributionSong(event.slug, event.isAlbum).subscribe(
      (res: any) => {
        if (res.success) {
          this.global.reload();
        } else {
          event.element.querySelector('mat-error').textContent = res.error;
        }
      },
      (err: HttpErrorResponse) => {
        event.element.querySelector('mat-error').textContent = 'Server-side error occurred!';
      }
    );
  }
}
